<template>
  <div class="yapp-root">
    <!-- Header -->
    <div class="yapp-header">
      <div class="d-flex align-center" style="cursor: pointer" @click="router.push({ name: 'Chat' })">
        <v-icon color="white" class="mr-2">mdi-chat</v-icon>
        <span class="text-white font-weight-bold text-body-1">Yapp</span>
      </div>

      <div class="d-flex align-center">
        <!-- Clinic selector (multi-clinic users) -->
        <v-btn
          v-if="needsClinicSelector && selectedClinicId"
          variant="text"
          color="white"
          size="small"
          @click="goToLobby"
        >
          <v-icon size="small" class="mr-1">mdi-swap-horizontal</v-icon>
          <span class="d-none d-sm-inline">{{ currentClinicLabel }}</span>
        </v-btn>

        <!-- Reportes (admin only) -->
        <v-btn
          v-if="hasAllClinicsAccess"
          icon
          variant="text"
          color="white"
          size="small"
          @click="router.push({ name: 'Reportes' })"
        >
          <v-icon size="small">mdi-chart-bar</v-icon>
          <v-tooltip activator="parent" location="bottom">Reportes</v-tooltip>
        </v-btn>

        <!-- User menu -->
        <v-menu location="bottom end">
          <template #activator="{ props }">
            <v-btn icon variant="text" color="white" v-bind="props" size="small" class="ml-1">
              <v-avatar size="28" color="rgba(255,255,255,0.2)">
                <span class="text-caption font-weight-bold text-white">
                  {{ userInitials }}
                </span>
              </v-avatar>
            </v-btn>
          </template>
          <v-card min-width="200">
            <v-card-text class="pb-0">
              <div class="text-subtitle-2 font-weight-medium">
                {{ authStore.user?.full_name || authStore.user?.username || 'Usuario' }}
              </div>
              <div class="text-caption text-grey">
                {{ authStore.userRole }}
              </div>
            </v-card-text>
            <v-divider class="mt-2" />
            <v-list density="compact">
              <v-list-item @click="handleLogout" prepend-icon="mdi-logout" title="Cerrar sesion" />
            </v-list>
          </v-card>
        </v-menu>
      </div>
    </div>

    <!-- Chat content (full height remaining) -->
    <div class="yapp-body">
      <ClinicLobby
        v-if="showLobby"
        @select-clinic="onClinicChange"
      />
      <router-view
        v-else
        :selected-clinic-id="selectedClinicId"
        @clinic-change="onClinicChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useClinicsStore } from '@/stores/clinics'
import { useChatStore } from '@/stores/chat'
import ClinicLobby from '@/components/chat/ClinicLobby.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const clinicsStore = useClinicsStore()
const chatStore = useChatStore()

const selectedClinicId = ref(null)

const hasAllClinicsAccess = computed(() => authStore.hasAllClinicsAccess)

const needsClinicSelector = computed(() => {
  const userClinics = authStore.assignedClinics
  if (authStore.hasAllClinicsAccess) return true
  return userClinics && userClinics.length > 1
})

const isOnChat = computed(() => route.name === 'Chat' || route.name === undefined)
const showLobby = computed(() => isOnChat.value && !selectedClinicId.value && needsClinicSelector.value)

const currentClinicLabel = computed(() => {
  if (!selectedClinicId.value) return 'Seleccionar'
  const clinic = clinicsStore.getClinicById(selectedClinicId.value)
  return clinic?.name || clinic?.clinic_name || `Clinica ${selectedClinicId.value}`
})

const userInitials = computed(() => {
  const name = authStore.user?.full_name || authStore.user?.username || '?'
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
})

const onClinicChange = (clinicId) => {
  selectedClinicId.value = clinicId
  chatStore.setCurrentClinic(clinicId)
}

const goToLobby = () => {
  selectedClinicId.value = null
  chatStore.unsubscribeFromMessages()
  chatStore.currentConversation = null
  chatStore.conversations = []
}

const handleLogout = async () => {
  await authStore.logout()
  router.push({ name: 'Login' })
}

onMounted(async () => {
  await authStore.initializeAuth()

  if (!authStore.isAuthenticated) {
    router.push({ name: 'Login' })
    return
  }

  if (!clinicsStore.clinics.length) {
    try {
      await clinicsStore.fetchClinics()
    } catch (error) {
      console.error('Error loading clinics:', error)
    }
  }

  // Auto-seleccionar clinica para usuarios de 1 sola clinica
  const userClinics = authStore.assignedClinics
  if (!authStore.hasAllClinicsAccess && userClinics) {
    const clinicIds = userClinics.map(c => typeof c === 'object' ? c.id : c)
    if (clinicIds.length === 1) {
      onClinicChange(clinicIds[0])
    }
  }
})
</script>

<style scoped>
.yapp-root {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f0f2f5;
}

.yapp-header {
  background: #075E54;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.yapp-body {
  flex: 1;
  overflow: hidden;
}
</style>
